import { useEffect } from 'react'
import { X, Info } from 'lucide-react'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import type { ZakatType, ZakatCalculationResult } from '@/types/zakat'

interface ZakatHowToModalProps {
  isOpen: boolean
  zakatType: ZakatType
  result: ZakatCalculationResult | null
  onClose: () => void
}

export default function ZakatHowToModal({
  isOpen,
  zakatType,
  result,
  onClose,
}: ZakatHowToModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const getHowToText = (): string => {
    if (!result) return ''

    switch (zakatType) {
      case 'money':
        return `المبلغ ضارب سعر 85 جرام ذهب عيار 24 أو أكثر
يتم خصم نسبة 2.5% من الناتج = قيمة الزكاة النهائية

مثال:
- المبلغ: ${result.totalAmount?.toLocaleString('ar-EG') || 0}
- النصاب: ${result.nisab > 0 ? result.nisab.toLocaleString('ar-EG') : '-'} (85 جرام × سعر الجرام)
- الزكاة: ${result.isZakatDue ? result.zakatAmount.toLocaleString('ar-EG') : 0} (2.5% من المبلغ)`

      case 'gold':
        return `وزن الذهب ضارب 85 جرام أو أكثر
يتم خصم نسبة 2.5% من الناتج = قيمة الزكاة النهائية

ملاحظة: يتم تحويل جميع العيارات إلى معادل عيار 24:
- عيار 18 = (18 ÷ 24) من الوزن
- عيار 21 = (21 ÷ 24) من الوزن
- عيار 24 = الوزن الكامل

مثال:
- النصاب: ${result.nisab} جرام من الذهب عيار 24
- الزكاة: ${result.isZakatDue ? result.zakatAmount.toLocaleString('ar-EG') : 0} (2.5% من قيمة الذهب)`

      case 'silver':
        return `وزن الفضة ضارب 595 جرام أو أكثر
يتم خصم نسبة 2.5% من الناتج = قيمة الزكاة النهائية

مثال:
- النصاب: ${result.nisab} جرام من الفضة
- الزكاة: ${result.isZakatDue ? result.zakatAmount.toLocaleString('ar-EG') : 0} (2.5% من قيمة الفضة)`

      case 'crops':
        const irrigationText = result.totalAmount ? 
          (result.zakatAmount / result.totalAmount * 100).toFixed(1) : '5'
        return `في حالة الزروع المسقية بتكلفة:
وزن الزروع ضارب 647 كيلو جرام أو أكثر
يتم خصم نسبة 5% من الناتج = قيمة الزكاة النهائية

في حالة الزروع المسقية بدون تكلفة مثل النخيل او بالأمطار:
وزن الزروع ضارب 647 كيلو جرام أو أكثر
يتم خصم نسبة 10% من الناتج = قيمة الزكاة النهائية

في حالة الزروع المسقية بالمناصفة:
وزن الزروع ضارب 647 كيلو جرام أو أكثر
يتم خصم نسبة 7.5% من الناتج = قيمة الزكاة النهائية

مثال:
- النصاب: ${result.nisab} كيلو جرام
- نسبة الزكاة: ${irrigationText}%
- الزكاة: ${result.isZakatDue ? result.zakatAmount.toLocaleString('ar-EG') : 0}`

      case 'livestock':
        const livestockInfo = result.zakatDescription 
          ? `الزكاة المستحقة: ${result.zakatDescription}`
          : ''
        return `زكاة الأنعام تعتمد على جداول مفصلة حسب نوع وعدد الحيوانات:

${livestockInfo ? livestockInfo + '\n\n' : ''}عدد رؤوس الإبل:
- 5-9: 1 شاة
- 10-14: 2 شاة
- 15-19: 3 شاة
- 20-24: 4 شاة
- 25-35: 1 بنت مخاض
- 36-45: 1 بنت لبون
- 46-60: 1 حِقة
- 61-75: 1 جذعة
- 76-90: 2 بنت لبون
- 91-120: 2 حِقة
- أكثر من 120: بنت لبون لكل 40 أو حِقة لكل 50

عدد رؤوس الغنم:
- 40-120: 1 شاة
- 121-200: 2 شاة
- 201-399: 3 شاة
- 400 أو أكثر: عدد الرؤوس ÷ 100

عدد رؤوس البقر:
- 30-39: 1 بقرة عمر سنة
- 40: 1 بقرة عمر سنتين
- أكثر من 40: بقرة عمر سنة لكل 30 أو بقرة عمر سنتين لكل 40

ملاحظة: زكاة الأنعام تُعطى كحيوانات وليس مال`

      case 'freelance':
        return `صافي الربح ضارب سعر 85 جرام ذهب عيار 24 أو أكثر
يتم خصم نسبة 2.5% من الناتج = قيمة الزكاة النهائية

صافي الربح = الإيرادات - الأجور ومصاريف التشغيل - نفقات المعيشة - الديون

مثال:
- صافي الربح: ${result.totalAmount?.toLocaleString('ar-EG') || 0}
- النصاب: ${result.nisab > 0 ? result.nisab.toLocaleString('ar-EG') : '-'} (85 جرام × سعر الجرام)
- الزكاة: ${result.isZakatDue ? result.zakatAmount.toLocaleString('ar-EG') : 0} (2.5% من صافي الربح)`

      default:
        return ''
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="zakat-howto-title"
      >
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto relative bg-white dark:bg-gray-800">
          <button
            onClick={onClose}
            className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                <Info className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h2
                id="zakat-howto-title"
                className="text-2xl font-bold text-gray-900 dark:text-gray-100"
              >
                كيفية حساب الزكاة
              </h2>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 mb-6">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-medium leading-relaxed">
                {getHowToText() || 'لا توجد معلومات متاحة'}
              </pre>
            </div>

            <Button
              variant="primary"
              size="lg"
              onClick={onClose}
              className="w-full"
            >
              موافق
            </Button>
          </div>
        </Card>
      </div>
    </>
  )
}

